import { z } from 'zod';
import { can } from './access';
import type { CoordinatorRole } from './constants';

/**
 * Notes between the organisation and the marshal team about a volunteer — the
 * Organizacija tab (SPEC §1.1L). Which side a note came from is the author's
 * role at the time, and the two sides owe each other different things:
 *
 * - The marshal team owes an answer. A thread waits on it while its newest
 *   note is the organisation's, and any reply from the team settles it.
 * - The organisation owes nothing. A reply from the team is news until an
 *   organizer has opened the thread since.
 *
 * Were both sides treated as owing, every reply would open a blocker on the
 * other side, which only another note could clear.
 */
export type NoteSide = 'organizacija' | 'redarji';

export function noteSide(role: CoordinatorRole): NoteSide {
  // Whoever can change the plan is the marshal team; whoever cannot is the organisation.
  return can(role, 'plan:write') ? 'redarji' : 'organizacija';
}

export interface NoteLike {
  authorRole: CoordinatorRole;
  createdAt: Date | string;
}

/** Whether the marshal team owes this thread an answer. */
export function awaitsTeam(notes: readonly NoteLike[]): boolean {
  const newest = newestOf(notes);
  return newest !== undefined && noteSide(newest.authorRole) === 'organizacija';
}

/** When the team last wrote to this thread, or null — what an organizer's "unread" compares with. */
export function newestTeamNoteAt(notes: readonly NoteLike[]): Date | null {
  const team = notes.filter((n) => noteSide(n.authorRole) === 'redarji');
  const newest = newestOf(team);
  return newest ? new Date(newest.createdAt) : null;
}

/** Whether an organizer who last opened the thread at `readAt` has a reply they have not seen. */
export function unreadForOrganizer(notes: readonly NoteLike[], readAt: Date | string | null): boolean {
  const teamAt = newestTeamNoteAt(notes);
  if (!teamAt) return false;
  return readAt === null || teamAt.getTime() > new Date(readAt).getTime();
}

function newestOf<T extends NoteLike>(notes: readonly T[]): T | undefined {
  let newest: T | undefined;
  for (const n of notes) {
    if (!newest || new Date(n.createdAt).getTime() >= new Date(newest.createdAt).getTime()) newest = n;
  }
  return newest;
}

/**
 * Whether a volunteer's thread asks something of this viewer, from the rollup
 * the roster carries: an answer owed for the marshal team, an unopened reply
 * for an organizer. What the roster badge and the tab badge show.
 */
export function noteNeedsViewer(
  role: CoordinatorRole,
  rollup: { notesAwaitingTeam?: boolean; notesUnreadForMe?: boolean },
): boolean {
  return noteSide(role) === 'redarji' ? Boolean(rollup.notesAwaitingTeam) : Boolean(rollup.notesUnreadForMe);
}

export const volunteerNoteInputSchema = z.object({
  body: z.string().trim().min(1).max(2000),
});
export type VolunteerNoteInput = z.infer<typeof volunteerNoteInputSchema>;

/** An organizer has read the thread up to and including this team note. */
export const volunteerNoteReadSchema = z.object({
  upToNoteId: z.uuid(),
});
